/* assets/cart.js */

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      if (!cartItems) return;
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();

    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') ||
      document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));

    // IMPORTANT:
    // Do NOT auto-fetch /cart.js here anymore.
    // Drawer exists on every page -> this caused an unnecessary request and "slow load" feeling.
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') return;
      this.onCartUpdate();
      // If event includes cartData, use it (no extra fetch)
      if (event.cartData) this.updateProgressBar(event.cartData);
      else this.updateProgressBar(); // best-effort fallback (main cart pages)
    });

    // Only auto-update on the real cart page component (not drawer)
    if (this.tagName === 'CART-ITEMS') {
      this.updateProgressBar();
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement?.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) targetElement.replaceWith(sourceElement);
          }
        })
        .catch((e) => console.error(e));
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          if (sourceQty) this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => console.error(e));
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items')?.dataset?.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
    ].filter((s) => s.section); // avoid undefined section ids
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          if (quantityElement) quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);

        const cartDrawerWrapper = document.querySelector('cart-drawer');
        if (cartDrawerWrapper) {
          cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);
          // Keep latest total in drawer dataset (helps open() progressbar update)
          if (typeof parsedState.total_price !== 'undefined') {
            cartDrawerWrapper.dataset.cartTotalCents = String(parsedState.total_price);
          }
        }

        this.getSectionsToRender().forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container) return;

          const elementToReplace = container.querySelector(section.selector) || container;
          elementToReplace.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);
        });

        const updatedValue = parsedState.items?.[line - 1]?.quantity;
        let message = '';
        if (quantityElement && items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value, 10)) {
          if (typeof updatedValue === 'undefined') message = window.cartStrings?.error || '';
          else message = (window.cartStrings?.quantityError || '').replace('[quantity]', updatedValue);
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);

        if (lineItem && name && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        // Update progress bar WITHOUT extra fetch
        this.updateProgressBar(parsedState);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId });
      })
      .catch((error) => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings?.error || 'Error';
        console.error(error);
      })
      .finally(() => {
        this.disableLoading(line);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);

    if (lineItemError) {
      const t = lineItemError.querySelector('.cart-item__error-text');
      if (t) t.innerHTML = message;
    }

    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');

    if (!cartStatus) return;

    cartStatus.setAttribute('aria-hidden', false);
    setTimeout(() => cartStatus.setAttribute('aria-hidden', true), 1000);
  }

  getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  /**
   * Update free shipping progress bar.
   * If cartData passed in, uses it (no network).
   * If not, falls back to /cart.js.
   */
/**
 * Funcție îmbunătățită pentru actualizarea bării de progres
 * Actualizează INSTANT și în mod vizual atractiv
 */
updateProgressBar(cartData) {
  // Scope to the correct bar (drawer bar when called from cart-drawer-items)
  const drawerRoot = this.closest('cart-drawer');
  const root = drawerRoot || document;

  const el = root.querySelector('#free-shipping-threshold');
  const progressEl = root.querySelector('#progress');
  const messageEl = root.querySelector('#free-shipping-message');
  if (!el || !progressEl || !messageEl) return;

  const thresholdRON = parseFloat(el.dataset.thresholdRon || '150');
  const thresholdMDL = parseFloat(el.dataset.thresholdMdl || '600');
  const currencySymbol = el.dataset.currencySymbol || 'lei';
  
  // FIX: Detectează corect MDL/lei - ambele ar trebui să folosească threshold-ul MDL
  const isMDL = (currencySymbol === 'L' || currencySymbol === 'lei' || currencySymbol === 'MDL');
  const threshold = isMDL ? thresholdMDL : thresholdRON;
  const currencyToShow = isMDL ? 'lei' : 'RON';

  const apply = (totalPriceCents) => {
    const cartTotal = (totalPriceCents || 0) / 100;

    // Actualizează threshold-ul afisat
    el.textContent = `${threshold} ${currencyToShow}`;

    const pct = threshold > 0 ? Math.min((cartTotal / threshold) * 100, 100) : 0;
    
    // Actualizare INSTANT cu animație
    progressEl.style.width = `${pct}%`;

if (threshold > 0 && cartTotal >= threshold) {
  messageEl.innerHTML = 'Congratulations, you have <strong>free shipping</strong>!';
  messageEl.classList.add('success');
  progressEl.style.setProperty('background', 'linear-gradient(90deg, #059669 0%, #10b981 100%)', 'important');
} else {
  messageEl.classList.remove('success');
  progressEl.style.setProperty('background', 'linear-gradient(90deg, #8C6A4F 0%, #a67c5d 100%)', 'important');
  
  const remaining = Math.max(threshold - cartTotal, 0);
  messageEl.innerHTML = `You're <strong>${remaining.toFixed(2)} ${currencyToShow}</strong> away from FREE standard shipping`;
}
  };
  

  // Prioritate 1: Folosește cartData dacă este disponibil (INSTANT)
  if (cartData && typeof cartData.total_price !== 'undefined') {
    apply(cartData.total_price);
    return;
  }

  // Prioritate 2: Folosește dataset de pe cart-drawer (pentru deschidere)
  if (drawerRoot && drawerRoot.dataset.cartTotalCents) {
    apply(parseInt(drawerRoot.dataset.cartTotalCents, 10));
    return;
  }

  // Prioritate 3: Fallback la /cart.js doar dacă e absolut necesar
  fetch('/cart.js')
    .then((r) => r.json())
    .then((cart) => apply(cart.total_price))
    .catch((e) => console.error('Error updating the progress bar:', e));
}


  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    if (document.activeElement) document.activeElement.blur();
    if (this.lineItemStatusElement) this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    if (mainCartItems) mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'change',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
